'use client';

import { gsap, useGSAP, NOT_REDUCED, playOnEnter } from '@/components/sections/kit/motion';

/**
 * Page-scoped choreography helpers for the /compare/* dossiers, built on the
 * site motion kit (playOnEnter's IO + self-healing insurance). The dossier's
 * entrance grammar: timelines are created PAUSED with the DOM resting on the
 * finished state (SSR/no-JS/reduced-motion all see the final page), then play
 * once on first viewport enter.
 */

/**
 * Build a paused entrance animation for `ref`'s subtree and play it on first
 * enter. `build` runs only under `prefers-reduced-motion: no-preference` and
 * must return a PAUSED animation (or nothing to opt out).
 */
export function useChoreo(
  ref: React.RefObject<HTMLElement | null>,
  build: (root: HTMLElement) => gsap.core.Animation | undefined | void,
) {
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const root = ref.current;
        if (!root) return;
        const anim = build(root);
        if (!anim) return;
        return playOnEnter(anim, root);
      });
    },
    { scope: ref },
  );
}

/**
 * The dossier's rubber-stamp settle for Stamp/pill elements. The pre-play
 * hidden state is opacity-only (set immediately), NOT the scaled from-state:
 * a from() tween would pre-render `scale: 1.5`, and a long stamp scaled up
 * while waiting below the fold adds real horizontal overflow on mobile.
 */
export function stampIn(
  tl: gsap.core.Timeline,
  targets: gsap.TweenTarget,
  position?: gsap.Position,
) {
  gsap.set(targets, { opacity: 0 });
  tl.fromTo(
    targets,
    { scale: 1.5, rotate: -8, opacity: 0 },
    {
      scale: 1,
      rotate: 0,
      opacity: 1,
      duration: 0.35,
      ease: 'power3.in',
      transformOrigin: '50% 50%',
      immediateRender: false,
    },
    position,
  );
}

/** Cross-component "jump to table category" request (hero → table). */
export const JUMP_EVENT = 'compare-jump';

export function requestJump(cat: number) {
  window.dispatchEvent(new CustomEvent<number>(JUMP_EVENT, { detail: cat }));
}
